import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Button, Space, Card, Row, Col, Grid, Typography, Tag, Divider } from "antd";
import type { ColumnsType } from "antd/es/table";

import { AppState } from "store";
import { initializeServiceCentres, deleteServiceCentre } from "store/actions";
import { setModalName } from "store/actions/common-actions";
import { ServiceCentre } from "types/store";
import { deleteIcon, editIcon } from "../../../svgs";

import "styles/components/tab-content/service-center-content/service-content.scss";
import { ActionMenu } from "./cell-render";
import useDebounce from "utils/hooks";

const { useBreakpoint } = Grid;
const { Text, Title } = Typography;

export default function ServiceCentresContent() {
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  
  const { serviceCentres, search } = useSelector<AppState, AppState["serviceCentres"]>(
    (state) => state.serviceCentres
  );
  const {
    common: { userType = 1 },
  } = useSelector<AppState, AppState>((state) => state);

  // Determine if we should show mobile layout
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;
  const debounceSearchString = useDebounce(search, 1000);

  useEffect(() => {
    dispatch(initializeServiceCentres());
  }, [dispatch, debounceSearchString]);

  // Filter service centres based on search
  const filteredServiceCentres = useMemo(() => {
    if (!serviceCentres) return [];
    
    const searchTerm = search?.toLowerCase().trim();
    
    // If no search term, return all service centres
    if (!searchTerm) {
      return serviceCentres;
    }

    return serviceCentres.filter((centre) => {
      // Search in service centre name
      const nameMatch = centre.serviceCentreName
        ?.toLowerCase()
        .includes(searchTerm);

      // Search in contact name
      const contactNameMatch = centre.contactName
        ?.toLowerCase()
        .includes(searchTerm);

      // Search in services offered
      const servicesMatch = centre.servicesOffered
        ?.toLowerCase()
        .includes(searchTerm);

      // Return true if any field matches
      return (
        nameMatch ||
        contactNameMatch ||
        servicesMatch
      );
    });
  }, [serviceCentres, search]);

  const onAdd = () => {
    dispatch(setModalName("add_service_centre"));
  };

  const onEdit = (record: ServiceCentre) => {
    dispatch({
      type: "SET_SELECTED_SERVICE_CENTRE",
      payload: record,
    });
    dispatch(setModalName("edit_service_centre"));
  };

  const onDelete = (record: ServiceCentre) => {
    if (record.id) {
      dispatch({
        type: "SET_SELECTED_SERVICE_CENTRE",
        payload: record,
      });
      dispatch(setModalName("delete_service_centre"));
    }
  };

 const renderServices = (
  servicesOffered: string | undefined,
  isMobileView: boolean = false
) => {
  if (!servicesOffered) return null;

  const services = servicesOffered
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  if (!services.length) return null;

  return (
    <div className={`services-tags ${isMobileView ? "mobile" : "inline"}`}>
      <Space size={[8, 8]} wrap>
        {services.map((service, index) => (
          <Tag key={index} className="service-tag">
            {highlightText(service, search)}
          </Tag>
        ))}
      </Space>
    </div>
  );
};

   

  // Desktop/Tablet Table Columns
  const columns: ColumnsType<ServiceCentre> = [
    {
      title: "Name",
      dataIndex: "serviceCentreName",
      key: "serviceCentreName",
      ellipsis: false,
      width: 200,
    },
    {
      title: "Address",
      key: "address",
      ellipsis: false,
      responsive: ["md"],
      width: 200,
      render: (_: unknown, record: ServiceCentre) =>
        record.address?.formattedaddress || "",
    },
    {
      title: "Contact Name",
      dataIndex: "contactName",
      key: "contactName",
      responsive: ["lg"],
      width: 200,
    },
    {
      title: "Contact Number",
      dataIndex: "contactNumber",
      key: "contactNumber",
      responsive: ["sm"],
      width: 150,
    },
    {
      title: "Services Offered",
      dataIndex: "servicesOffered",
      key: "servicesOffered",
      responsive: ["xl"],
      width: 200,
      ellipsis: false,
      render: (_: unknown, record: ServiceCentre) => 
        renderServices(record.servicesOffered, false),
    },
    {
      title: " ",
      dataIndex: "action",
      render: (_: unknown, record: ServiceCentre) =>
        ActionMenu(_ as any, { raw: record } as any),
      align: "right",
      className: "action-menu open-link --service-centre",
    },
  ];

  // Helper function to highlight search text
  const highlightText = (text: string | undefined, searchTerm: string | undefined) => {
    if (!text || !searchTerm?.trim()) return text;
    
    const term = searchTerm.toLowerCase().trim();
    const index = text.toLowerCase().indexOf(term);
    
    if (index === -1) return text;
    
    const before = text.slice(0, index);
    const match = text.slice(index, index + term.length);
    const after = text.slice(index + term.length);
    
    return (
      <>
        {before}
        <mark className="search-highlight">{match}</mark>
        {after}
      </>
    );
  };

  // Mobile Card Component 
  const ServiceCentreCard = ({ record }: { record: ServiceCentre }) => (
    <Card 
      className="service-centre-card"
      size="small"
    >
      {/* Card Header */}
      <div className="card-header">
        <Title level={5} className="card-title">
          {highlightText(record.serviceCentreName, search)}
        </Title>
      </div>
      
      {/* Contact Details */}
      <div className="card-details">
        {record.address?.formattedaddress && (
          <div className="detail-item">
            <Text className="detail-label">Address</Text>
            <Text className="detail-value address-value">
              {highlightText(record.address.formattedaddress, search)}
            </Text>
          </div>
        )}

        {record.contactName && (
          <div className="detail-item">
            <Text className="detail-label">Contact</Text>
            <Text className="detail-value">
              {highlightText(record.contactName, search)}
            </Text>
          </div>
        )}

        {record.contactNumber && (
          <div className="detail-item">
            <Text className="detail-label">Phone</Text>
            <a href={`tel:${record.contactNumber}`} className="detail-value phone-link">
              {highlightText(record.contactNumber, search)}
            </a>
          </div>
        )}
      </div>

      {record.servicesOffered && (
        <>
          <Divider className="services-divider" />
          <div className="services-section">
            <Text className="services-label">Services Offered</Text>
            {renderServices(record.servicesOffered, true)}
          </div>
        </>
      )}

      {/* Action Buttons - MOBILE ONLY */}
      {userType !== 6 && (
        <>
          <Divider className="actions-divider" />
          <div className="card-actions">
            <Button 
              type="default"
              onClick={() => onEdit(record)}
              className="card-action-btn edit-btn"
            >
              <span className="btn-icon">{editIcon}</span>
              <span className="btn-text">Edit</span>
            </Button>
            
            <Button 
              type="default"
              danger 
              onClick={() => onDelete(record)}
              className="card-action-btn delete-btn"
            >
              <span className="btn-icon">{deleteIcon}</span>
              <span className="btn-text">Delete</span>
            </Button>
          </div>
        </>
      )}
    </Card>
  );

  // Mobile Layout
  const MobileLayout = () => (
    <div className="mobile-list">
      {filteredServiceCentres?.length ? (
        <Row gutter={[16, 16]}>
          {filteredServiceCentres.map((centre) => (
            <Col xs={24} sm={12} key={centre.id}>
              <ServiceCentreCard record={centre} />
            </Col>
          ))}
        </Row>
      ) : (
        <div className="empty-state" style={{ textAlign: "center" }}>
          <Text type="secondary">
            {search?.trim() 
              ? `No service centers found for "${search}"` 
              : "No service centers found"
            }
          </Text>
        </div>
      )}
    </div>
  );

// Replace TableLayout with this:
const TableLayout = () => (
  <Table<ServiceCentre>
    rowKey="id"
    dataSource={filteredServiceCentres}
    columns={columns}
    pagination={false}
    //scroll={{ x: isTablet ? 600 :  'max-content' }}
    size={isTablet ? "small" : "middle"}
    className="service-centres-table"
    rowClassName="service-centre-row"
    locale={{
      emptyText: (
        <div className="empty-state web" style={{ textAlign: "center", width: "100%" }}>
          <Text type="secondary">
            {search?.trim()
              ? `No service centers found for "${search}"`
              : "No service centers found"}
          </Text>
        </div>
      ),
    }}
  />
);

  return (
    <div className="service-centres-content">
      {isMobile ? <MobileLayout /> : <TableLayout />}
    </div>
  );
}